import { axios } from '@/services/axios';
import {
    Box,
    FormLabel,
    HStack,
    Skeleton,
    SkeletonCircle,
    Stack,
    VStack,
    FormErrorMessage,
    FormControl,
    Text,
} from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';
import DropDown from '@/components/forms/new/formikFormFields/DropDown';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    LoadingIndicator,
} from 'components/v4';
import { Input } from '@/components/v4';
import FormBase from '@/components/forms/new/FormBase';
import useFieldValidation from 'hooks/useFieldValidation';
import { isEmpty } from 'lodash';
import walkthroughIds from 'pages/services/walkthroughIds';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { validationSchemaVD } from '../edit/schema';
import style from '../edit/styles';
import { useDefinedAttributes } from './DefinedAttributesContext';
import DefinedAttributes from './attributes/definedAttributes';
import useDefinedAttributeValidation from './attributes/useDefinedAttributeValidation';
import config from '@/config.json';
import {
    descriptionStyling,
    formLabelStyling,
    stackSpacing,
    textBoxStyling,
} from 'components/forms/new/formStyles';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import BreadcrumbTrail from 'components/BreadcrumbTrail';

export default function EditVendorDefined({
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) {
    return (
        <EditVendorDefinedInner
            data={data}
            handleBackButton={handleBackButton}
            level={level}
            visibleScopeOptions={visibleScopeOptions}
        />
    );
}

function EditVendorDefinedInner({
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isVisibleScopeHovered, setIsVisibleScopeHovered] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);
    const {
        currentWholesaler: wholesalerID,
        currentPartner: currentPartnerID,
    } = useSelector((state) => state.navigationLists);
    const { dispatch, attributesList, attributesErrors } =
        useDefinedAttributes();
    const { handleAttributeError, removeAttributeError } =
        useDefinedAttributeValidation();
    const DEFAULT_VENDOR_DEFINED_PAYLOAD = {
        id: data?.id || '',
        name:
            data?.name ||
            (level === 'partner'
                ? 'Vendor Defined Partner Profile'
                : 'Vendor Defined Wholesaler Profile'),
        serviceType: data?.serviceType || 'VendorDefined',
        serviceDefinitionID:
            data?.serviceDefinitionID || data?.serviceDefinition?.id || '',
        partnerID:
            level === 'partner'
                ? data?.partnerID || currentPartnerID
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : data?.wholesalerID || wholesalerID,
        visibleScope:
            typeof data?.visibleScope === 'number'
                ? data.visibleScope
                : level === 'partner'
                  ? 20
                  : 60,
    };
    const { ...ufElements } = walkthroughIds.serviceProfiles;

    const [modalState, setModalState] = useState(() => ({
        ...DEFAULT_VENDOR_DEFINED_PAYLOAD,
    }));
    const { validationErrors, validatefn } = useFieldValidation(
        modalState,
        validationSchemaVD,
    );

    // Query for vendor defined data
    const { isLoading: vendorDefinedLoading } = useQuery({
        queryKey: ['vendorDefined', data.serviceDefinition?.id],
        queryFn: async () => {
            const serviceDefinitionID = data.serviceDefinition?.id;
            const res = await axios.get(
                `serviceprofiles/vendordefined/${serviceDefinitionID}/${scope()}`,
                {
                    headers: {
                        'X-RequestScope': requiredScope,
                    },
                },
            );
            return res.data;
        },
        enabled: Boolean(data?.serviceDefinition?.id),
        onSuccess: (data) => {
            setModalState((prevState) => ({
                ...prevState,
                ...data,
            }));
            if (data.attributeList?.length > 0) {
                for (let att of data.attributeList) {
                    if (isEmpty(att.value)) {
                        att.value = isEmpty(att.defaultValue)
                            ? null
                            : att.defaultValue;
                    }
                }
            }
            dispatch({
                type: 'UPDATE_ATT_LIST',
                payload: data.attributeList,
            });
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
        },
        refetchOnWindowFocus: false,
        cacheTime: 0,
        staleTime: 0,
    });

    /*
     * A function to get the current scope and append it for API calls
     */
    const scope = () => {
        if (level === 'partner') {
            return `Partner/${data?.partnerID || currentPartnerID}`;
        } else {
            return `Wholesaler/${data?.wholesalerID || wholesalerID}`;
        }
    };

    // function to set modal state with a given payload
    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    const handleSubmit = async () => {
        setError(null);
        let hasError = false;

        if (!validatefn()) {
            setError({
                response: {
                    data: {
                        message:
                            'There are some validation errors in the form. Please check and try again.',
                    },
                },
            });
            return;
        }

        // check for any attributes errors
        if (attributesErrors.length > 0) {
            hasError = true;
        }

        // check for required attributes
        if (attributesList?.length > 0) {
            attributesList.forEach((att, index) => {
                const validation = JSON.parse(
                    att.definedAttributeValidationType,
                );

                // if required, input cannot be empty
                if (validation.Required && isEmpty(att.value)) {
                    handleAttributeError(index, 'Field is required.');
                    hasError = true;
                } else {
                    removeAttributeError(index);
                }
            });
        }
        if (hasError) return;

        setIsLoading(true);
        try {
            console.log(modalState);
            const serviceDefinitionID = modalState.serviceDefinitionID;
            // wholesaler creates a service profile for partner
            if (level === 'partner') {
                await axios.put(
                    `serviceprofiles/vendordefined/${serviceDefinitionID}/partner`,
                    {
                        id: modalState.id,
                        name: modalState.name,
                        partnerID: modalState.partnerID,
                        serviceDefinitionID: serviceDefinitionID,
                        visibleScope: modalState.visibleScope,
                        attributeList: attributesList,
                    },
                    {
                        headers: {
                            'X-RequestScope': requiredScope,
                        },
                    },
                );
            }
            // GA creates for wholesaler
            else {
                console.log('this is wholesaler update');
                await axios.put(
                    `serviceprofiles/vendordefined/${serviceDefinitionID}/wholesaler`,
                    {
                        id: modalState.id,
                        name: modalState.name,
                        wholesalerID: modalState.wholesalerID,
                        serviceDefinitionID: serviceDefinitionID,
                        visibleScope: modalState.visibleScope,
                        attributeList: attributesList,
                    },
                    {
                        headers: {
                            'X-RequestScope': requiredScope,
                        },
                    },
                );
            }
            toast.success(
                `Successfully updated a ${
                    level === 'partner' ? 'Partner' : 'Wholesaler'
                } Vendor Defined Service Profile!`,
            );
            handleBackButton();
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`Edit ${modalState.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                customClick={true}
                initialValues={DEFAULT_VENDOR_DEFINED_PAYLOAD}
                onBack={handleBackButton}
                isLoading={isLoading}
                finalFormSubmit={handleSubmit}>
                {() => (
                    <>
                        {error && (
                            <Alert status="error" sx={{ marginTop: '1rem' }}>
                                <AlertIcon />
                                <AlertDescription>
                                    {error?.response?.data?.message ||
                                        'An error occured. Please try again later.'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {/* Service Name Field */}

                        <Box sx={style.fieldContainer}>
                            <FormControl
                                sx={{ margin: '0 !important' }}
                                isRequired={true}
                                isInvalid={!!validationErrors?.name}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}>
                                {vendorDefinedLoading ? (
                                    <Skeleton h={10} borderRadius={4} />
                                ) : (
                                    <Stack
                                        direction="row"
                                        spacing={stackSpacing}
                                        alignItems="flex-start"
                                        sx={{ width: '775px' }}>
                                        <Box>
                                            <FormLabel
                                                sx={formLabelStyling(darkMode)}>
                                                Service Name
                                            </FormLabel>
                                            <Text
                                                sx={descriptionStyling}
                                                maxWidth={250}>
                                                Enter a name for this Vendor
                                                Defined profile.
                                            </Text>
                                        </Box>
                                        <Box
                                            style={{
                                                flex: 1,
                                                minWidth: '450px',
                                                marginTop: '16px',
                                            }}>
                                            <VStack
                                                width="100%"
                                                alignItems="normal">
                                                <Input
                                                    id="service-name"
                                                    name="serviceName"
                                                    placeholder="Please enter a value"
                                                    value={modalState.name}
                                                    onChange={(e) =>
                                                        handleChangeState({
                                                            name: e.target
                                                                .value,
                                                        })
                                                    }
                                                    sx={textBoxStyling}
                                                    data-walkthroughid={
                                                        ufElements.ufServiceNameInput
                                                    }
                                                />
                                                <FormErrorMessage
                                                    display="flex"
                                                    sx={{
                                                        marginTop:
                                                            '0 !important',
                                                        fontSize: '10px',
                                                    }}
                                                    justifyContent="flex-end">
                                                    {validationErrors?.name}
                                                </FormErrorMessage>
                                            </VStack>
                                        </Box>
                                        {isHovered && (
                                            <FieldToolTip
                                                label="Enter the service name"
                                                ariaLabel="vendor-defined-service-name-tip"
                                                left="25px"
                                                top="23px"
                                            />
                                        )}
                                    </Stack>
                                )}
                            </FormControl>
                        </Box>
                        {/* Visible Scope Field */}
                        <Box sx={style.fieldContainer}>
                            <FormControl
                                sx={{ margin: '0 0 24px 0 !important' }}
                                onMouseEnter={() =>
                                    setIsVisibleScopeHovered(true)
                                }
                                onMouseLeave={() =>
                                    setIsVisibleScopeHovered(false)
                                }
                                isRequired={true}>
                                <Stack
                                    direction="row"
                                    spacing={stackSpacing}
                                    alignItems="flex-start"
                                    sx={{ width: '775px' }}>
                                    <Box>
                                        <FormLabel sx={formLabelStyling()}>
                                            Visible Scope
                                        </FormLabel>
                                        <Text
                                            sx={descriptionStyling}
                                            maxWidth={250}>
                                            Select the visibility and service
                                            creation level.
                                        </Text>
                                    </Box>
                                    <Box
                                        style={{
                                            flex: 1,
                                            minWidth: '450px',
                                        }}>
                                        <VStack
                                            width="100%"
                                            alignItems="normal">
                                            {vendorDefinedLoading ? (
                                                <Skeleton
                                                    h={10}
                                                    borderRadius={4}
                                                />
                                            ) : modalState.visibleScope <=
                                              requiredScope ? (
                                                <DropDown
                                                    currentId={
                                                        modalState.visibleScope
                                                    }
                                                    data={visibleScopeOptions.map(
                                                        (option) => ({
                                                            id: option.value,
                                                            name: option.label,
                                                        }),
                                                    )}
                                                    shownValue="name"
                                                    alphabetical={false}
                                                    handleSelectChange={(
                                                        item,
                                                    ) =>
                                                        handleChangeState({
                                                            visibleScope:
                                                                item?.id,
                                                        })
                                                    }
                                                    darkMode={darkMode}
                                                    width="100%"
                                                />
                                            ) : (
                                                <Input
                                                    isDisabled
                                                    value={
                                                        config.scope.json[
                                                            modalState
                                                                .visibleScope
                                                        ]
                                                    }
                                                    sx={{
                                                        ...textBoxStyling,
                                                        marginTop: '34px',
                                                        paddingTop: 0,
                                                        borderRadius: '5px',
                                                    }}
                                                />
                                            )}
                                        </VStack>
                                    </Box>
                                    {isVisibleScopeHovered && (
                                        <FieldToolTip
                                            label="Select the visibility and service creation level."
                                            ariaLabel="vendor-defined-visible-scope-tip"
                                            left="25px"
                                            top="23px"
                                        />
                                    )}
                                </Stack>
                            </FormControl>
                        </Box>
                        {/* Dynamic Attributes - context controlled */}
                        {(() => {
                            return vendorDefinedLoading;
                        })() ? (
                            <Box>
                                {[...Array(3)].map((_, index) => (
                                    <Stack key={index} mb={3}>
                                        <HStack>
                                            <Skeleton h={5} w="25%" />
                                            <SkeletonCircle h={5} w={5} />
                                        </HStack>
                                        <Skeleton h={10} borderRadius={4} />
                                        <Skeleton h={5} w="15%" />
                                    </Stack>
                                ))}
                            </Box>
                        ) : (
                            <DefinedAttributes requiredScope />
                        )}
                    </>
                )}
            </FormBase>
        </>
    );
}
